/**
 * JobClose AI · Slice 2T · Levi 1.0 Intelligence
 * ---------------------------------------------------
 * deriveLeviRecommendations(facts, opts) → { primary, also_noticed,
 *                                             warnings, follow_ups }
 *
 * Pure function. No React. No fetches. No timers. No LLM calls.
 *
 * DESIGN GUARANTEES
 * -----------------
 * 1. Every candidate recommendation is derived from a small conjunction
 *    of facts. Candidates are evaluated in PARALLEL, not in a workflow
 *    order. A contractor who skipped Double Check and jumped to invoices
 *    will see BOTH surface.
 * 2. `primary` is chosen by a relevance score, not by workflow position.
 *    Warnings outrank actions outrank info. Ties are broken by a stable
 *    candidate priority so the output is deterministic.
 * 3. `also_noticed` never suppresses other truthful observations. The
 *    "primary" is removed from this list to avoid duplication; every
 *    other candidate that fired is kept (up to a soft cap of 6).
 * 4. The interface is extensible: each candidate returns an object
 *    including `id`, `tone`, and `action_id`. Later Levi reasoning
 *    layers can consume the same shape without a rewrite.
 */

const TONE_RANK = { warning: 3, action: 2, positive: 1, info: 0 };

/**
 * Recommendation candidates. Each candidate is a pure function of the
 * facts object. It returns `null` when the fact set does not warrant
 * the recommendation, or a Recommendation object otherwise.
 *
 * IMPORTANT — data-honesty:
 *   A candidate MUST return `null` when the supporting facts are
 *   undefined. It must NEVER assume a missing fact means "false".
 */
const CANDIDATES = [
  // ---------- Warnings (highest priority) ----------
  {
    id: "estimate_double_check_findings",
    build(f) {
      if (f.estimate_double_check_ran !== true) return null;
      const red = Number(f.estimate_double_check_red_count || 0);
      const yellow = Number(f.estimate_double_check_yellow_count || 0);
      if (red <= 0 && yellow <= 0) return null;
      return {
        id: "estimate_double_check_findings",
        tone: red > 0 ? "warning" : "action",
        copy_key: red > 0
          ? "levi_project_snapshot.rec.dc_findings_red"
          : "levi_project_snapshot.rec.dc_findings_yellow",
        copy_args: { red, yellow },
        action_id: "run_double_check",
      };
    },
  },
  {
    id: "invoice_payment_due",
    build(f) {
      if (f.invoices_any_payment_due !== true) return null;
      return {
        id: "invoice_payment_due",
        tone: "warning",
        copy_key: "levi_project_snapshot.rec.invoice_payment_due",
        action_id: "open_first_unpaid_invoice",
      };
    },
  },
  {
    id: "contract_awaiting_signature",
    build(f) {
      if (f.contracts_any_awaiting_signature !== true) return null;
      return {
        id: "contract_awaiting_signature",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.contract_awaiting_signature",
        action_id: "open_first_pending_contract",
      };
    },
  },
  {
    id: "estimate_unpriced_lines",
    build(f) {
      const n = Number(f.estimate_unpriced_lines_count || 0);
      if (n <= 0) return null;
      return {
        id: "estimate_unpriced_lines",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.estimate_unpriced",
        copy_args: {
          count: n,
          names: Array.isArray(f.estimate_unpriced_line_names) ? f.estimate_unpriced_line_names : [],
        },
        action_id: "jump_missing_pricing",
      };
    },
  },
  {
    id: "estimate_double_check_stale",
    build(f) {
      if (f.estimate_double_check_stale !== true) return null;
      return {
        id: "estimate_double_check_stale",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.dc_stale",
        action_id: "run_double_check",
      };
    },
  },
  {
    id: "estimate_double_check_not_run",
    build(f) {
      if (f.estimate_has_line_items !== true) return null;
      if (f.estimate_double_check_ran === true) return null;
      if (Number(f.estimate_unpriced_lines_count || 0) > 0) return null; // deferred to unpriced
      return {
        id: "estimate_double_check_not_run",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.dc_not_run",
        action_id: "run_double_check",
      };
    },
  },
  // ---------- Estimate creation / scope ----------
  {
    id: "no_estimate_yet",
    build(f) {
      if (typeof f.estimates_count !== "number") return null;
      if (f.estimates_count > 0) return null;
      if (f.project_is_closed === true) return null;
      return {
        id: "no_estimate_yet",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.no_estimate",
        action_id: "create_estimate",
      };
    },
  },
  {
    id: "estimate_has_no_line_items",
    build(f) {
      if (typeof f.estimates_count !== "number" || f.estimates_count <= 0) return null;
      if (f.estimate_has_line_items === true) return null;
      return {
        id: "estimate_has_no_line_items",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.estimate_empty",
        action_id: "open_estimate",
      };
    },
  },
  // ---------- Proposal / contract / invoice progression ----------
  {
    id: "proposal_approved_no_contract",
    build(f) {
      if (f.proposals_any_approved !== true) return null;
      if (typeof f.contracts_count !== "number") return null;
      if (f.contracts_count > 0) return null;
      return {
        id: "proposal_approved_no_contract",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.approved_no_contract",
        action_id: "new_contract",
      };
    },
  },
  {
    id: "proposal_awaiting_response",
    build(f) {
      if (f.proposals_awaiting_response !== true) return null;
      return {
        id: "proposal_awaiting_response",
        tone: "info",
        copy_key: "levi_project_snapshot.rec.proposal_awaiting",
        action_id: "open_first_shared_proposal",
      };
    },
  },
  {
    id: "no_proposal_yet",
    build(f) {
      if (typeof f.proposals_count !== "number" || f.proposals_count > 0) return null;
      if (f.estimate_has_line_items !== true) return null;
      if (Number(f.estimate_unpriced_lines_count || 0) > 0) return null;
      if (f.project_is_closed === true) return null;
      // Only nudge when a Double Check has actually been considered.
      if (f.estimate_double_check_ran !== true && f.estimate_double_check_ack_present !== true) return null;
      return {
        id: "no_proposal_yet",
        tone: "info",
        copy_key: "levi_project_snapshot.rec.no_proposal",
        action_id: "new_proposal",
      };
    },
  },
  {
    id: "contract_signed_no_invoice",
    build(f) {
      if (f.contracts_any_signed !== true) return null;
      if (typeof f.invoices_count !== "number" || f.invoices_count > 0) return null;
      return {
        id: "contract_signed_no_invoice",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.signed_no_invoice",
        action_id: "new_invoice",
      };
    },
  },
  {
    id: "invoices_all_paid_no_closeout",
    build(f) {
      if (f.invoices_all_paid !== true) return null;
      if (typeof f.closeout_packet_count !== "number") return null;
      if (f.closeout_packet_count > 0) return null;
      return {
        id: "invoices_all_paid_no_closeout",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.paid_no_closeout",
        action_id: "generate_closeout",
      };
    },
  },
  // ---------- Intake completeness ----------
  {
    id: "intake_customer_contact_missing",
    build(f) {
      if (f.customer_name_present !== true) return null;
      const hasEmail = f.customer_email_present === true;
      const hasPhone = f.customer_phone_present === true;
      if (hasEmail || hasPhone) return null;
      return {
        id: "intake_customer_contact_missing",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.customer_contact_missing",
        action_id: "edit_intake_notes",
      };
    },
  },
  {
    id: "intake_missing_narrative",
    build(f) {
      // Only surface when we know enough to be confident nothing is present.
      const knownEmpty =
        f.job_description_present === false &&
        f.typed_notes_present === false &&
        f.voice_transcript_present === false;
      if (!knownEmpty) return null;
      // photos_present may be undefined; when it is undefined, still surface.
      if (f.photos_present === true) return null;
      if (f.project_is_closed === true) return null;
      return {
        id: "intake_missing_narrative",
        tone: "action",
        copy_key: "levi_project_snapshot.rec.no_narrative",
        action_id: "edit_intake_notes",
      };
    },
  },
  // ---------- Informational ----------
  {
    id: "customer_no_docs_shared_yet",
    build(f) {
      if (f.customer_access_no_docs_shared !== true) return null;
      if (typeof f.estimates_count !== "number" || f.estimates_count === 0) return null;
      if (f.project_is_draft === true) return null;
      if (f.project_is_closed === true) return null;
      return {
        id: "customer_no_docs_shared_yet",
        tone: "info",
        copy_key: "levi_project_snapshot.rec.customer_no_docs_shared",
        action_id: null,
      };
    },
  },
  {
    id: "project_closed",
    build(f) {
      if (f.project_is_closed !== true) return null;
      return {
        id: "project_closed",
        tone: "positive",
        copy_key: "levi_project_snapshot.rec.project_closed",
        action_id: null,
      };
    },
  },
];

// Stable priority tie-breaker so `primary` selection is deterministic
// even when multiple candidates share the highest tone rank.
const PRIORITY_ORDER = CANDIDATES.reduce((acc, c, i) => {
  acc[c.id] = i;
  return acc;
}, {});

const _score = (rec) => {
  if (!rec) return -1;
  const t = TONE_RANK[rec.tone] ?? 0;
  // Convert priority into a fractional decrement so higher tones always win.
  return t * 1000 - (PRIORITY_ORDER[rec.id] ?? 999);
};

/**
 * @param {object} facts     - Output of deriveLeviFacts.
 * @param {object} [opts]    - `{ maxAlsoNoticed: 6 }`
 * @returns {{primary, also_noticed, warnings, follow_ups}}
 */
export function deriveLeviRecommendations(facts, opts) {
  const options = opts || {};
  const maxAlso = typeof options.maxAlsoNoticed === "number" ? options.maxAlsoNoticed : 6;
  const f = facts || {};

  const fired = [];
  for (const c of CANDIDATES) {
    let rec;
    try {
      rec = c.build(f);
    } catch (_) {
      rec = null;
    }
    if (rec) fired.push(rec);
  }

  const warnings = fired.filter((r) => r.tone === "warning");

  // Sort by score desc.
  const ranked = [...fired].sort((a, b) => _score(b) - _score(a));

  let primary = null;
  if (ranked.length > 0) {
    primary = ranked[0];
  } else {
    // Nothing to say → all_clean.
    primary = {
      id: "all_clean",
      tone: "positive",
      copy_key: "levi_project_snapshot.rec.all_clean",
      action_id: null,
    };
  }

  const alsoNoticed = ranked
    .filter((r) => r.id !== primary.id)
    .slice(0, Math.max(0, maxAlso));

  // Follow-ups are conservative for Slice 2T; they invite the contractor
  // to Ask Levi about broad topics without recommending any route.
  const followUps = [];
  if (typeof f.estimates_count === "number" && f.estimates_count > 0) {
    followUps.push({
      id: "ask_scope",
      copy_key: "levi_project_snapshot.followup.scope",
    });
  }
  if (typeof f.proposals_count === "number" && f.proposals_count > 0) {
    followUps.push({
      id: "ask_customer_status",
      copy_key: "levi_project_snapshot.followup.customer_status",
    });
  }

  return {
    primary,
    also_noticed: alsoNoticed,
    warnings,
    follow_ups: followUps,
  };
}

export default deriveLeviRecommendations;
